import alt from 'components/Dispatcher';
import generateAsyncActions from 'utils/generateAsyncActions';
import {ActionsBase, DispatchAll, IncludeResultCallbacks} from '@store/@types/alt';
import {Vote} from 'records/CallDetails';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {Call} from '@store/call-card/Call';
import {CallCardSource} from './CallCardSource';
import Scorecard from 'records/Scorecard';
import {List} from 'immutable';
import {
  CallAsset,
  Segment,
  Speaker,
  TopicMatch,
  TranscriptBlock,
} from '@store/call-card/CallCard.types';
import {UserCallActivity} from '@store/call-card/UserCallActivity';
import {UserComment} from 'records/UserComment';
import {Matchable, TopicId, TopicFilter, Topic} from '@store/topic-keyword/TopicKeyword.types';

interface AsyncActions {
  getCall: (id: FileUploadId) => Promise<Call | undefined>;
  getCallSource: (id: FileUploadId) => Promise<[CallAsset[], FileUploadId]>;
  getScorecards: (id: FileUploadId) => Promise<[List<Scorecard>, FileUploadId]>;
  getSpeakers: (id: FileUploadId) => Promise<[List<Speaker>, FileUploadId]>;
  getUserCallActivity: (id: FileUploadId) => Promise<[List<UserCallActivity>, FileUploadId]>;
  getTranscript: (id: FileUploadId) => Promise<[List<TranscriptBlock>, FileUploadId]>;
  getSegments: (id: FileUploadId) => Promise<[List<Segment>, FileUploadId]>;
  getComments: (id: FileUploadId) => Promise<[List<UserComment>, FileUploadId]>;
  getMatches: (id: FileUploadId, topic: Matchable) => Promise<[List<TopicMatch>, FileUploadId]>;
  setVote: (id: FileUploadId, vote: 'Up' | 'Down') => Promise<[Vote, FileUploadId]>;
  deleteVote: (id: FileUploadId) => Promise<FileUploadId>;
  setCoachMe: (id: FileUploadId, coachMe: boolean) => Promise<[boolean, FileUploadId]>;
  getTopics: (
    id: FileUploadId,
    extraTopicIds: List<TopicId>
  ) => Promise<[List<Topic>, List<TopicId>, FileUploadId]>;
}

interface SyncActions {
  toggleTopic: (id: FileUploadId, topicId: TopicId) => void;
  resetMatches: () => void;
  getTopicFilter: (id: FileUploadId) => TopicFilter;
  setTopicFilter: (id: FileUploadId, filter: TopicFilter) => void;
  resetTopics: (id: FileUploadId) => void;
}

export interface CallCardActionsInterface
  extends IncludeResultCallbacks<AsyncActions>,
    SyncActions {}

class CallCardActions
  extends ActionsBase<AsyncActions, SyncActions>()
  implements DispatchAll<AsyncActions>
{
  constructor() {
    super();
    this.generateActions(
      'toggleTopic',
      'resetMatches',
      'getTopicFilter',
      'setTopicFilter',
      'resetTopics',
      ...generateAsyncActions<AsyncActions>(
        'getCall',
        'getCallSource',
        'getScorecards',
        'getSpeakers',
        'getUserCallActivity',
        'getTranscript',
        'getSegments',
        'getComments',
        'getMatches',
        'setVote',
        'deleteVote',
        'setCoachMe',
        'getTopics'
      )
    );
  }

  getCall(id) {
    return dispatch => {
      dispatch();
      return CallCardSource.getCall(id).then(this.getCallSuccess, this.getCallError);
    };
  }

  getCallSource(id) {
    return dispatch => {
      dispatch();
      return CallCardSource.getCallSource(id).then(
        this.getCallSourceSuccess,
        this.getCallSourceError
      );
    };
  }

  getScorecards(id) {
    return dispatch => {
      dispatch();
      return CallCardSource.getScorecards(id).then(
        this.getScorecardsSuccess,
        this.getScorecardsError
      );
    };
  }

  getSpeakers(id) {
    return dispatch => {
      dispatch();
      return CallCardSource.getSpeakers(id).then(this.getSpeakersSuccess, this.getSpeakersError);
    };
  }

  getUserCallActivity(id) {
    return dispatch => {
      dispatch();
      return CallCardSource.getUserCallActivity(id).then(
        this.getUserCallActivitySuccess,
        this.getUserCallActivityError
      );
    };
  }

  getTranscript(id) {
    return dispatch => {
      dispatch();
      return CallCardSource.getTranscript(id).then(
        this.getTranscriptSuccess,
        this.getTranscriptError
      );
    };
  }

  getSegments(id) {
    return dispatch => {
      dispatch();
      return CallCardSource.getSegments(id).then(this.getSegmentsSuccess, this.getSegmentsError);
    };
  }

  getComments(id) {
    return dispatch => {
      dispatch();
      return CallCardSource.getComments(id).then(this.getCommentsSuccess, this.getCommentsError);
    };
  }

  setVote(id, vote) {
    return dispatch => {
      dispatch([id, vote]);
      return CallCardSource.setVote(id, vote).then(this.setVoteSuccess, this.setVoteError);
    };
  }

  deleteVote(id) {
    return dispatch => {
      dispatch(id);
      return CallCardSource.deleteVote(id).then(this.deleteVoteSuccess, this.deleteVoteError);
    };
  }

  setCoachMe(id, coachMe) {
    return dispatch => {
      dispatch();
      return CallCardSource.setCoachMe(id, coachMe).then(
        this.setCoachMeSuccess,
        this.setCoachMeError
      );
    };
  }

  getMatches(id, topicId) {
    return dispatch => {
      dispatch();
      return CallCardSource.getMatches(id, topicId).then(
        this.getMatchesSuccess,
        this.getMatchesError
      );
    };
  }

  getTopics(id, extraTopicIds) {
    return dispatch => {
      dispatch();
      return CallCardSource.getTopics(id, extraTopicIds).then(
        this.getTopicsSuccess,
        this.getTopicsError
      );
    };
  }
}

export default alt.createActions<AsyncActions, SyncActions>(CallCardActions);
